<template>
  <Modal
    v-model="visible"
    title="意见反馈"
    :width="700"
    class-name="dark-modal feedback-modal"
    :footer-hide="true"
    @on-visible-change="handleVisibleChange"
  >
    <div class="feedback-modal-content">
      <Tabs v-model="activeTab">
        <!-- 提交反馈 -->
        <TabPane label="我要反馈" name="submit">
          <div class="submit-section">
            <div class="quota-info">
              <Alert show-icon>
                您的意见是我们进步的动力！被采纳的反馈将获得 <b>2折优惠券</b> 奖励。
              </Alert>
            </div>

            <Form ref="formRef" :model="formData" :rules="rules" label-position="top">
              <FormItem label="反馈内容" prop="content">
                <Input
                  v-model="formData.content"
                  type="textarea"
                  :rows="6"
                  placeholder="请详细描述您的建议或遇到的问题（至少10个字符）..."
                  :maxlength="2000"
                  show-word-limit
                  :disabled="remainingQuota <= 0"
                />
              </FormItem>
              <FormItem>
                <Button
                  type="primary"
                  long
                  size="large"
                  @click="handleSubmit"
                  :loading="submitting"
                  :disabled="remainingQuota <= 0"
                >
                  {{ remainingQuota <= 0 ? '今日次数已用完' : '提交反馈' }}
                </Button>
              </FormItem>
            </Form>
          </div>
        </TabPane>

        <!-- 历史记录 -->
        <TabPane label="反馈记录" name="history">
          <div class="history-section">
            <div v-if="loading" class="loading-wrapper">
              <Spin fix></Spin>
            </div>
            <div v-else-if="feedbacks.length > 0" class="history-list">
              <div v-for="item in feedbacks" :key="item.id" class="history-item">
                <div class="item-header">
                  <span class="item-time">{{ formatDate(item.creationTime) }}</span>
                  <Tag v-if="item.isAccepted" color="success">已采纳</Tag>
                </div>
                <div class="item-content">{{ item.content }}</div>
                
                <div v-if="item.adminReply" class="item-reply">
                  <div class="reply-title">管理员回复：</div>
                  <div class="reply-text">{{ item.adminReply }}</div>
                </div>

                <div v-if="item.isRewarded" class="item-reward">
                  <Icon type="md-gift" color="#19be6b" /> 获得奖励：2折优惠券
                </div>
              </div>
              
              <div class="pagination-wrapper" v-if="totalCount > 0">
                <Page
                  :total="totalCount"
                  :current="currentPage"
                  :page-size="pageSize"
                  size="small"
                  @on-change="handlePageChange"
                />
              </div>
            </div>
            <div v-else class="empty-state">
              <Icon type="ios-list-box-outline" size="48" />
              <p>暂无反馈记录</p>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { Message } from 'view-ui-plus'
import { feedbackService, type FeedbackDto } from '@/services/feedback'
import moment from 'moment'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits(['update:modelValue'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const activeTab = ref('submit')
const submitting = ref(false)
const remainingQuota = ref(3)
const formRef = ref()
const formData = reactive({
  content: ''
})

const rules = {
  content: [
    { required: true, message: '请输入反馈内容', trigger: 'blur' },
    { min: 10, message: '最少输入10个字符', trigger: 'blur' }
  ]
}

// 历史记录
const loading = ref(false)
const feedbacks = ref<FeedbackDto[]>([])
const totalCount = ref(0)
const currentPage = ref(1)
const pageSize = ref(5)

const handleVisibleChange = (val: boolean) => {
  if (val) {
    loadQuota()
    if (activeTab.value === 'history') {
      loadHistory()
    }
  }
}

watch(activeTab, (val) => {
  if (val === 'history') {
    loadHistory()
  }
})

const loadQuota = async () => {
  try {
    remainingQuota.value = await feedbackService.getRemainingQuota()
  } catch (e) {
    console.error(e)
  }
}

const handleSubmit = async () => {
  formRef.value.validate(async (valid: boolean) => {
    if (!valid) return
    
    submitting.value = true
    try {
      await feedbackService.create({ content: formData.content })
      Message.success('反馈提交成功')
      formData.content = ''
      loadQuota()
      // 切换到历史记录查看
      activeTab.value = 'history'
      loadHistory()
    } catch (error: any) {
      Message.error(error.message || '提交失败')
    } finally {
      submitting.value = false
    }
  })
}

const loadHistory = async () => {
  loading.value = true
  try {
    const res = await feedbackService.getMyFeedbacks({
      skipCount: (currentPage.value - 1) * pageSize.value,
      maxResultCount: pageSize.value
    })
    feedbacks.value = res.items || []
    totalCount.value = res.totalCount
  } catch (e) {
    Message.error('加载历史记录失败')
  } finally {
    loading.value = false
  }
}

const handlePageChange = (page: number) => {
  currentPage.value = page
  loadHistory()
}

const formatDate = (date: string) => {
  return moment(date).format('YYYY-MM-DD HH:mm')
}
</script>

<style scoped>
.feedback-modal-content {
  min-height: 400px;
}

.submit-section {
  padding: 16px 0;
}

.quota-info :deep(.ivu-alert) {
  background: rgba(0, 242, 96, 0.1);
  border: 1px solid rgba(0, 242, 96, 0.2);
}

.quota-info :deep(.ivu-alert-message) {
  color: #f8fafc;
}

.quota-info :deep(.ivu-alert-desc) {
  color: #cbd5e1;
}

.quota-info :deep(.ivu-alert-icon) {
  color: #00f260;
}

.quota-count {
  font-weight: bold;
  color: #00f260;
  font-size: 16px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}

.history-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
}

.item-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  color: #999;
}

.item-content {
  font-size: 14px;
  color: #e2e8f0;
  white-space: pre-wrap;
  line-height: 1.5;
}

.item-reply {
  margin-top: 12px;
  background: rgba(45, 140, 240, 0.1);
  border-radius: 4px;
  padding: 8px;
}

.reply-title {
  color: #2d8cf0;
  font-size: 12px;
  margin-bottom: 4px;
}

.reply-text {
  color: #c5dcf3;
  font-size: 13px;
}

.item-reward {
  margin-top: 8px;
  font-size: 12px;
  color: #19be6b;
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #666;
}

.pagination-wrapper {
  margin-top: 16px;
  text-align: center;
}
</style>